import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { InstallButton } from "@/components/InstallButton";
import { SessionGuard } from "@/components/SessionGuard";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LayoutDashboard, Users, Calendar, ClipboardCheck, UserCircle, CreditCard, FolderOpen, Inbox, LogOut, BellRing, BookOpen, ClipboardList, Globe, Settings, Menu, X, Video, LifeBuoy, KeyRound } from "lucide-react";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, roles: ["admin","collaboratore","docente","studente"], tid: "nav-dashboard" },
  { to: "/app/anagrafiche", icon: UserCircle, label: "Anagrafiche", roles: ["admin","collaboratore"], tid: "nav-anagrafiche" },
  { to: "/app/materie", icon: BookOpen, label: "Materie", roles: ["admin","collaboratore"], tid: "nav-materie" },
  { to: "/app/classi", icon: Users, label: "Classi", roles: ["admin","collaboratore","docente","studente"], tid: "nav-classi" },
  { to: "/app/materiali", icon: FolderOpen, label: "Materiali Didattici", roles: ["admin","collaboratore","docente","studente"], tid: "nav-materiali" },
  { to: "/app/calendario", icon: Calendar, label: "Calendario Lezioni", roles: ["admin","collaboratore","docente","studente"], tid: "nav-calendario" },
  { to: "/app/conferenze", icon: Video, label: "Conferenze BBB", roles: ["admin"], tid: "nav-conferenze" },
  { to: "/app/test", icon: ClipboardList, label: "Area Test", roles: ["admin","collaboratore","docente","studente"], tid: "nav-test" },
  { to: "/app/presenze", icon: ClipboardCheck, label: "Registro Presenze", roles: ["admin","collaboratore"], tid: "nav-presenze" },
  { to: "/app/registro-pagamenti", icon: CreditCard, label: "Registrazione Pagamenti", roles: ["admin","collaboratore"], tid: "nav-registro-pagamenti" },
  { to: "/app/piani-rate", icon: CreditCard, label: "Piani Rate", roles: ["studente"], tid: "nav-piani-rate" },
  { to: "/app/solleciti", icon: BellRing, label: "Solleciti", roles: ["admin","collaboratore"], tid: "nav-solleciti" },
  { to: "/app/iscrizioni", icon: Inbox, label: "Pre-iscrizioni Online", roles: ["admin","collaboratore"], tid: "nav-iscrizioni" },
  { to: "/app/corsi-pubblici", icon: Globe, label: "Corsi Pubblici", roles: ["admin","collaboratore"], tid: "nav-corsi-pubblici" },
  { to: "/app/impostazioni", icon: Settings, label: "Impostazioni", roles: ["admin","collaboratore"], tid: "nav-impostazioni" },
];

export default function DashboardLayout() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pcur, setPcur] = useState(""); const [pnp, setPnp] = useState(""); const [pnp2, setPnp2] = useState(""); const [psaving, setPsaving] = useState(false);
  useEffect(() => { fetch(`${API}/`).then(r=>r.json()).then(d=>setAppVersion(d.version||"")).catch(()=>{}); }, []);
  const roleLabels = { admin: "Direzione / Amministrazione", collaboratore: "Collaboratore", docente: "Docente Formatore", studente: "Allievo / Studente" };

  const handleLogout = async () => { await logout(); nav("/login"); };

  const openGuida = async () => {
    setOpen(false);
    try {
      const token = localStorage.getItem("isteform_token");
      const r = await fetch(`${API}/guida`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Impossibile aprire la guida"); }
  };

  const changePwd = async () => {
    if (pnp.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (pnp !== pnp2) { toast.error("Le password non coincidono"); return; }
    setPsaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pcur, new_password: pnp });
      if (data.token) localStorage.setItem("isteform_token", data.token);
      try { const me = await api.get("/auth/me"); setUser(me.data); } catch { /* sessione mantenuta via cookie */ }
      toast.success("Password aggiornata correttamente");
      setPwdOpen(false); setPcur(""); setPnp(""); setPnp2("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore cambio password"); }
    finally { setPsaving(false); }
  };

  const items = NAV.filter(n => {
    if (!n.roles.includes(user?.role)) return false;
    if (user?.role !== "collaboratore") return true;
    const ECON = ["nav-registro-pagamenti","nav-solleciti","nav-iscrizioni","nav-corsi-pubblici"];
    const sez = Array.isArray(user?.sezioni_consentite) ? user.sezioni_consentite : [];
    if (n.tid === "nav-dashboard") return true;
    if (ECON.includes(n.tid)) return sez.includes(n.tid);
    return sez.length === 0 || sez.includes(n.tid);
  });

  const SidebarContent = () => (
    <>
      <div className="bg-gradient-to-r from-[#F7941E] via-[#00A896] to-[#1F4E79] p-4 shadow-md flex items-center justify-between">
        <div>
          <img src="/isteform-logo-white.svg" alt="Centro Studi ISTEFORM" className="h-9 w-auto"/>
          <div className="text-[9px] uppercase tracking-widest text-white/85 mt-1.5">Portale Gestionale</div>
        </div>
        <button onClick={()=>setOpen(false)} className="lg:hidden text-white/90 hover:text-white p-1" data-testid="sidebar-close-btn" aria-label="Chiudi menu"><X className="w-6 h-6"/></button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map(n => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.tid} onClick={()=>setOpen(false)}
            className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[#F7941E] text-white font-semibold shadow-sm" : "text-slate-300 hover:text-white hover:bg-[#1E3A5F]/60"}`}>
            <n.icon className="w-4 h-4 flex-shrink-0"/>
            <span>{n.label}</span>
          </NavLink>
        ))}
        <button type="button" onClick={openGuida} data-testid="nav-guida"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-slate-300 hover:text-white hover:bg-[#1E3A5F]/60">
          <LifeBuoy className="w-4 h-4 flex-shrink-0"/>
          <span>Guida / Manuale</span>
        </button>
        <button type="button" onClick={()=>{ setOpen(false); setPwdOpen(true); }} data-testid="nav-cambia-password"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-slate-300 hover:text-white hover:bg-[#1E3A5F]/60">
          <KeyRound className="w-4 h-4 flex-shrink-0"/>
          <span>Cambia password</span>
        </button>
      </nav>
      <div className="p-4 bg-[#0B2138] border-t border-[#1E3A5F] space-y-3">
        <div>
          <div className="text-xs text-slate-400 mb-1">{roleLabels[user?.role]}</div>
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
        </div>
        <InstallButton variant="sidebar"/>
        <Button variant="outline" size="sm" onClick={handleLogout} className="w-full border-[#F7941E] text-[#F7941E] hover:bg-[#F7941E] hover:text-white bg-transparent" data-testid="logout-btn">
          <LogOut className="w-3 h-3 mr-2"/> Esci
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      <SessionGuard/>
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent data-testid="cambia-password-dialog">
          <DialogHeader><DialogTitle>Cambia password</DialogTitle><DialogDescription>Aggiorna la password del tuo account. Resterai connesso su questo dispositivo.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Password attuale</Label><Input type="password" value={pcur} onChange={e=>setPcur(e.target.value)} data-testid="cambia-pwd-current"/></div>
            <div><Label>Nuova password</Label><Input type="password" value={pnp} onChange={e=>setPnp(e.target.value)} placeholder="Almeno 8 caratteri" data-testid="cambia-pwd-new"/></div>
            <div><Label>Conferma nuova password</Label><Input type="password" value={pnp2} onChange={e=>setPnp2(e.target.value)} data-testid="cambia-pwd-new2"/></div>
          </div>
          <DialogFooter><Button onClick={changePwd} disabled={psaving} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="cambia-pwd-save">{psaving ? "Aggiornamento..." : "Cambia password"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 bg-[#0F2B48] border-r border-[#1E3A5F] flex-col" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Drawer mobile */}
      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={()=>setOpen(false)} data-testid="sidebar-overlay"/>}
      <aside className={`fixed z-50 inset-y-0 left-0 w-72 max-w-[85vw] bg-[#0F2B48] flex flex-col transform transition-transform duration-300 lg:hidden ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar-mobile">
        <SidebarContent />
      </aside>

      <main className="flex-1 overflow-y-auto min-w-0">
        <div className="sticky top-0 z-20 flex items-center gap-2 bg-white/90 backdrop-blur border-b border-[#E2E8F0] px-4 sm:px-6 py-2.5" data-testid="topbar">
          <button onClick={()=>setOpen(true)} className="lg:hidden p-1.5 -ml-1 text-[#0F2B48] hover:bg-stone-100 rounded-md" data-testid="menu-open-btn" aria-label="Apri menu"><Menu className="w-6 h-6"/></button>
          <img src="/isteform-logo.png" alt="ISTEFORM" className="h-7 w-auto lg:hidden"/>
          <div className="ml-auto flex items-center gap-2">
            {user?.role === "admin" && appVersion && <span className="text-[11px] font-mono text-stone-400" data-testid="app-version-badge">v{appVersion}</span>}
            {user && <NotificationBell />}
          </div>
        </div>
        <Outlet/>
      </main>
    </div>
  );
}
